import React from "react";
import styles from "./ai-image-generator/AiImageGenerator.module.css";
import { useAiImageGenerator } from "./ai-image-generator/useAiImageGenerator";
import { Header } from "./ai-image-generator/components/Header";
import ModeToggle from "./ai-image-generator/components/ModeToggle";
import ExportOptionsBar from "./ai-image-generator/components/ExportOptionsBar";
import { GeneratePanel } from "./ai-image-generator/components/GeneratePanel";
import { EnhancePanel } from "./ai-image-generator/components/EnhancePanel";
import { PreviewPanel } from "./ai-image-generator/components/PreviewPanel";
import { REFERENCE_BOX_VALUES, OUTPUT_SIZE_PRESETS, EXPORT_FORMATS } from "../../utils/imageEditor";

function AiImageGenerator({ productCategory, title = '', description = '', setOpenAiDialog }) {
	const api = useAiImageGenerator(productCategory, title, description);
	const {
		mode,
		loading,
		error,
		generatedUrls,
		processedUrls,
		previewUrl,
		selectedFeatures,
		hasGeneratedResults,
		hasProcessedResults,
		switchToEnhance,
		switchToGenerate,
		resetGenerated,
		resetProcessed,
		handleApplyGenerate,
		handleDownload,
	} = api;

	return (
		<div className={styles.root}>
			<Header onClose={() => setOpenAiDialog(false)} />

			<ModeToggle
				mode={mode}
				onEnhance={switchToEnhance}
				onGenerate={switchToGenerate}
			/>

			<div className={styles.scroll}>
				{mode === "enhance" && (
					<ExportOptionsBar
						useExportAndResolution={api.useExportAndResolution}
						setUseExportAndResolution={api.setUseExportAndResolution}
						exportFormat={api.exportFormat}
						setExportFormat={api.setExportFormat}
						exportQuality={api.exportQuality}
						setExportQuality={api.setExportQuality}
						globalOutputSize={api.globalOutputSize}
						setGlobalOutputSize={api.setGlobalOutputSize}
						maxWidth={api.maxWidth}
						setMaxWidth={api.setMaxWidth}
						maxHeight={api.maxHeight}
						setMaxHeight={api.setMaxHeight}
						referenceBox={api.referenceBox}
						setReferenceBox={api.setReferenceBox}
						exportFormats={EXPORT_FORMATS}
						outputSizePresets={OUTPUT_SIZE_PRESETS}
						referenceBoxValues={REFERENCE_BOX_VALUES}
					/>
				)}

				<div className={styles.grid}>
					<div className={styles.leftPanel}>
						{mode === "generate" ? (
							<GeneratePanel
								error={error}
								genPrompt={api.genPrompt}
								setGenPrompt={api.setGenPrompt}
								genSeed={api.genSeed}
								setGenSeed={api.setGenSeed}
								catalogPromptFromProduct={api.catalogPromptFromProduct}
								categoryPrompts={api.categoryPrompts}
								matchedCategory={api.matchedCategory}
								useExportAndResolution={api.useExportAndResolution}
								setUseExportAndResolution={api.setUseExportAndResolution}
								exportFormat={api.exportFormat}
								setExportFormat={api.setExportFormat}
								exportQuality={api.exportQuality}
								setExportQuality={api.setExportQuality}
								generateDisabled={api.generateDisabled}
								loading={loading}
								onGenerate={handleApplyGenerate}
							/>
						) : (
							<EnhancePanel
								error={error}
								imageFile={api.imageFile}
								previewUrl={previewUrl}
								fileInputRef={api.fileInputRef}
								isDragOver={api.isDragOver}
								selectedFeatures={api.selectedFeatures}
								featureOptions={api.featureOptions}
								expandAiBackgroundConflictNote={api.expandAiBackgroundConflictNote}
								loading={loading}
								applyDisabled={api.applyDisabled}
								onFileChange={api.onFileChange}
								onDrop={api.onDrop}
								setIsDragOver={api.setIsDragOver}
								clearImage={api.clearImage}
								toggleFeature={api.toggleFeature}
								setOption={api.setOption}
								onApply={api.handleApply}
								features={api.features}
							/>
						)}
					</div>

					<PreviewPanel
						mode={mode}
						generatedUrls={generatedUrls}
						processedUrls={processedUrls}
						previewUrl={previewUrl}
						loading={loading}
						error={error}
						selectedFeatures={selectedFeatures}
						hasGeneratedResults={hasGeneratedResults}
						hasProcessedResults={hasProcessedResults}
						onResetGenerated={resetGenerated}
						onResetProcessed={resetProcessed}
						onDownload={handleDownload}
					/>
				</div>
			</div>
		</div>
	);
}

export default AiImageGenerator;
